import { useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { api } from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";
import { keys as observedKeys } from "@/features/shared/widgets/observations-map/hooks/queries";
import { useTranslation } from "@/lib/i18n";

import {
  listTimeSeriesDatasetLabels,
  mapAllTimeSeriesParameters,
  type TimeSeriesCatalogueParameter,
} from "./map-all-time-series-parameters";

/** Same tuples as `weather-model-map/queries.ts` so CMS model lists share the cache. */
const modelKeys = {
  models: () => ["models"] as const,
  modelParameters: (modelId: string | undefined) =>
    ["models", modelId, "parameters"] as const,
};

const EMPTY_MODELS: { id: string; name: string }[] = [];

/**
 * Observed parameters plus every weather model's parameter list. Pending until
 * observed, models, and each per-model query have settled.
 */
export function useAllTimeSeriesParametersQuery(enabled = true) {
  const { t } = useTranslation();

  const observedQuery = useQuery({
    queryKey: observedKeys.observedParameters(),
    queryFn: () => api.parameters.listObservedParameters(),
    select: (response) => response.data,
    enabled,
  });

  const modelsQuery = useQuery({
    queryKey: modelKeys.models(),
    queryFn: () => unwrapAxiosData(api.parameters.listModels()),
    enabled,
  });

  const models = modelsQuery.data ?? EMPTY_MODELS;

  const modelParametersQuery = useQueries({
    queries: models.map((model) => ({
      queryKey: modelKeys.modelParameters(model.id),
      queryFn: (): Promise<TimeSeriesCatalogueParameter[]> =>
        unwrapAxiosData(api.parameters.listParametersForModel(model.id)),
      enabled: enabled && !!model.id,
    })),
    combine: (results) => ({
      byModelId: Object.fromEntries(
        models.map((model, index) => [model.id, results[index]?.data]),
      ) as Record<string, TimeSeriesCatalogueParameter[] | undefined>,
      isPending: results.some((result) => result.isPending),
      isError: results.some((result) => result.isError),
    }),
  });

  const isPending =
    observedQuery.isPending || modelsQuery.isPending || modelParametersQuery.isPending;
  const isError =
    observedQuery.isError || modelsQuery.isError || modelParametersQuery.isError;

  const data = useMemo(() => {
    if (isPending || isError || !observedQuery.data || !modelsQuery.data) {
      return undefined;
    }

    const parametersByModelId: Record<string, TimeSeriesCatalogueParameter[]> = {};
    for (const model of modelsQuery.data) {
      const parameters = modelParametersQuery.byModelId[model.id];
      if (!parameters) return undefined;
      parametersByModelId[model.id] = parameters;
    }

    return mapAllTimeSeriesParameters(
      observedQuery.data,
      modelsQuery.data,
      parametersByModelId,
      {
        observed: t("admin.widgets.fields.time_series.dataset.observed"),
        model: (name) => t("admin.widgets.fields.time_series.dataset.model", { name }),
      },
    );
  }, [
    isPending,
    isError,
    observedQuery.data,
    modelsQuery.data,
    modelParametersQuery.byModelId,
    t,
  ]);

  const datasetLabels = useMemo(() => {
    if (!modelsQuery.data) return [];
    return listTimeSeriesDatasetLabels(modelsQuery.data, {
      observed: t("admin.widgets.fields.time_series.dataset.observed"),
      model: (name) => t("admin.widgets.fields.time_series.dataset.model", { name }),
    });
  }, [modelsQuery.data, t]);

  /**
   * Maps each model parameter id to its owning model id. Observed parameters are
   * absent, so membership doubles as the observed/model discriminator that the
   * widget renderer needs to route series to the right data source.
   */
  const parameterModelIndex = useMemo(() => {
    const index: Record<string, string> = {};
    if (!modelsQuery.data) return index;
    for (const model of modelsQuery.data) {
      const parameters = modelParametersQuery.byModelId[model.id];
      if (!parameters) continue;
      for (const parameter of parameters) index[parameter.id] = model.id;
    }
    return index;
  }, [modelsQuery.data, modelParametersQuery.byModelId]);

  return { data, datasetLabels, parameterModelIndex, isPending, isError };
}
